import { useCallback } from 'react';

import { useNamedLogger } from '@eluve/logger';

type UsePersistentStorageReturn = {
  isSupported: boolean;
  requestPermission: () => Promise<boolean>;
};

// Configurable cache duration for denied permissions (in days)
const PERMISSION_DENIAL_CACHE_DAYS = 7;
const STORAGE_KEY = 'persistent-storage-denied-cache';

type PermissionDenialCache = {
  deniedAt: number;
  expiresAt: number;
};

const getCachedDenial = (): PermissionDenialCache | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const entry: PermissionDenialCache = JSON.parse(stored);

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return entry;
  } catch {
    // Invalid JSON or other error - clear and return null
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const cacheDenial = (): void => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const entry: PermissionDenialCache = {
    deniedAt: now,
    expiresAt: now + PERMISSION_DENIAL_CACHE_DAYS * 24 * 60 * 60 * 1000,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage quota exceeded or other error - ignore
  }
};

export const usePersistentStorage = (): UsePersistentStorageReturn => {
  const logger = useNamedLogger('usePersistentStorage');

  const isSupported =
    typeof navigator !== 'undefined' &&
    'storage' in navigator &&
    'persist' in navigator.storage;

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    try {
      // Check if already persisted
      const isPersistent = await navigator.storage.persisted();
      if (isPersistent) {
        // Clear any cached denial since we now have permission
        localStorage.removeItem(STORAGE_KEY);
        logger.info('Persistent storage already granted');
        return true;
      }

      // Check if we have a cached denial
      const cachedDenial = getCachedDenial();
      if (cachedDenial) {
        logger.info('Persistent storage permission denied (cached)', {
          deniedAt: new Date(cachedDenial.deniedAt).toISOString(),
          expiresAt: new Date(cachedDenial.expiresAt).toISOString(),
        });
        return false;
      }

      const granted = await navigator.storage.persist();

      if (granted) {
        logger.info('Persistent storage permission granted');
      } else {
        // Cache the denial to avoid repeated requests
        cacheDenial();
        logger.info('Persistent storage permission denied');
      }

      return granted;
    } catch (error) {
      logger.error('Failed to request persistent storage permission', {
        error,
      });
      cacheDenial();
      return false;
    }
  }, [isSupported, logger]);

  return {
    isSupported,
    requestPermission,
  };
};
